import {
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    Injector,
    Input,
    OnDestroy,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutocompleteFunction } from '../../../../core/common/entities/autocompleteFunction.entity';
import {
    BaseNestedFieldDefinition,
    FieldType,
    getParentDottedKey
} from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import {
    AIProfileField,
    ProfileField,
    ProfileFieldScope
} from '../../../../core/profiles/entities/profile-field.entity';
import { Profile } from '../../../../core/profiles/entities/profile.entity';
import { PartialRecord } from '../../../../core/records/entities/partial-record.class';
import { RecordInheritance } from '../../../../core/records/entities/record-inheritance.class';
import { FormValueRequired } from '../../../../form-controls/helpers/form-value-required';
import { Family } from '../../../../public_api';
import { ShInjectionPointDirective } from '../../../directives/injection-point.directive';
import { ShProfileConfigService } from '../../../services/profile-config.service';
import { ShMergeStrategyControlComponent } from '../../merge-strategy-control/merge-strategy-control.component';
import { ShBaseGenericInputFieldComponent } from '../base-generic-input-field/base-generic-input-field.component';
import { ShUnsupportedInputFieldComponent } from '../simple-input-fields/unsupported-input-field/unsupported-input-field.component';

export type GenericInputFieldDescriptor = {
    type: Type<ShBaseGenericInputFieldComponent>;
    config?: Record<string, any>;
};

export type InputTypes = [
    ExtendedBaseProfileField<BaseNestedFieldDefinition>,
    UntypedFormGroup,
    PartialRecord[],
    boolean,
    number,
    AutocompleteFunction<string> | undefined
];

@Component({
    standalone: false,
    selector: 'sh-generic-field',
    template: `<ng-template injection-point></ng-template>`
})
export class ShGenericInputFieldComponent implements OnDestroy {
    private componentRef!: ComponentRef<ShBaseGenericInputFieldComponent>;

    private readonly destroy$: Subject<void> = new Subject<void>();
    private readonly initialized$: Subject<boolean> = new Subject<boolean>();
    private readonly profileField$: Subject<ExtendedBaseProfileField> = new Subject();
    private readonly recordInheritance$: Subject<RecordInheritance> = new Subject();
    private readonly aiProfile$: BehaviorSubject<Profile | undefined> = new BehaviorSubject<Profile | undefined>(
        undefined
    );
    private readonly formGroup$: Subject<UntypedFormGroup> = new Subject();
    private readonly records$: Subject<PartialRecord[]> = new Subject();
    private readonly isBatchEdit$: Subject<boolean> = new Subject();
    private readonly canEditStandard$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private readonly canEditAdvanced$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private readonly disabledMessageKey$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private readonly autocompleteFct$: ReplaySubject<AutocompleteFunction | undefined> = new ReplaySubject(1);
    private readonly nestedIndex$: ReplaySubject<number> = new ReplaySubject<number>(1);

    @Input() set profileField(profileField: ExtendedBaseProfileField) {
        this.profileField$.next(profileField);
    }

    @Input() set formGroup(formGroup: UntypedFormGroup) {
        this.formGroup$.next(formGroup);
    }

    @Input() set records(records: PartialRecord[]) {
        this.records$.next(records);
    }

    @Input() set recordInheritance(recordInheritance: RecordInheritance) {
        if (recordInheritance) {
            this.recordInheritance$.next(recordInheritance);
        }
    }

    @Input() set aiProfile(profile: Profile) {
        if (profile) {
            this.aiProfile$.next(profile);
        }
    }

    @Input() set autocompleteFct(fct: AutocompleteFunction | undefined) {
        this.autocompleteFct$.next(fct);
    }

    @Input() set subFieldAutocompleteFcts(fcts: Record<string, AutocompleteFunction>) {
        if (fcts && this.componentRef?.instance) {
            this.componentRef.instance.subFieldAutocompleteFcts = fcts;
        }
    }

    @Input() set isBatchEdit(isBatchEdit: boolean) {
        this.isBatchEdit$.next(isBatchEdit);
    }

    @Input() set canEditStandard(canEditStandard: boolean) {
        this.canEditStandard$.next(canEditStandard);
    }

    @Input() set canEditAdvanced(canEditAdvanced: boolean) {
        this.canEditAdvanced$.next(canEditAdvanced);
    }

    @Input() set nestedIndex(nestedIndex: number) {
        this.nestedIndex$.next(nestedIndex);
    }

    @ViewChild(ShInjectionPointDirective, { static: true }) adHost!: ShInjectionPointDirective;

    constructor(
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly profileConfigService: ShProfileConfigService
    ) {
        this.setup();
    }

    private setup(): void {
        // Typing gets angry if we don't do this
        const sources$: Observable<InputTypes> = combineLatest([
            this.profileField$,
            this.formGroup$,
            this.records$,
            this.isBatchEdit$,
            this.nestedIndex$,
            this.autocompleteFct$
        ]) as Observable<InputTypes>;
        sources$
            .pipe(withLatestFrom(this.profileConfigService.getInputOverrides()), take(1), takeUntil(this.destroy$))
            .subscribe(([[profileField, control, records, isBatchEdit, nestedIndex, fct], overrides]) => {
                this.destroyExisting();
                this.initialize(overrides, profileField, control, records, isBatchEdit, nestedIndex, fct);
            });

        this.initIconConditions();
    }

    private destroyExisting(): void {
        this.componentRef?.destroy();
    }

    private initialize(
        overrides: Record<string, GenericInputFieldDescriptor>,
        profileField: ExtendedBaseProfileField,
        formGroup: UntypedFormGroup,
        records: PartialRecord[],
        isBatchEdit: boolean,
        nestedIndex: number,
        autocompleteFct: AutocompleteFunction | undefined
    ): void {
        const defaultMapping = this.profileConfigService.getDefaultInputMappings();

        const dottedKey = profileField.FieldDefinition.DottedKey;
        const fieldType = profileField.FieldDefinition.Type;
        const controlOverride = profileField.Control;
        const childControl = new UntypedFormControl();

        formGroup.addControl(dottedKey, childControl);

        if (!isBatchEdit && (profileField.Required || profileField.FieldDefinition.Required)) {
            childControl.setValidators(FormValueRequired);
        }

        this.disableIfNeeded(childControl, profileField);

        const { componentType, componentConfig } = this.resolveComponent(
            overrides,
            dottedKey,
            fieldType,
            controlOverride,
            defaultMapping
        );

        const typeToLoad = componentType ?? ShUnsupportedInputFieldComponent;
        const effectiveBatchEdit = !!componentType && isBatchEdit;

        return this.loadChildComponent(
            typeToLoad,
            profileField,
            formGroup,
            childControl,
            records,
            componentConfig,
            effectiveBatchEdit,
            nestedIndex,
            autocompleteFct
        );
    }

    private resolveComponent(
        overrides: Record<string, GenericInputFieldDescriptor>,
        dottedKey: string,
        fieldType: FieldType,
        controlOverride: string | undefined,
        defaultMapping: Record<FieldType, Type<ShBaseGenericInputFieldComponent>>
    ): { componentType?: Type<ShBaseGenericInputFieldComponent>; componentConfig: Record<string, any> } {
        if (controlOverride) {
            const parsed = this.parseControlOverride(controlOverride);
            if (typeof parsed === 'string') {
                return {
                    componentType: this.profileConfigService.overrideInputMapping[parsed] ?? defaultMapping[fieldType],
                    componentConfig: {}
                };
            }
            if (
                parsed &&
                Object.prototype.hasOwnProperty.call(parsed, 'type') &&
                Object.prototype.hasOwnProperty.call(parsed, 'config')
            ) {
                return {
                    componentType:
                        this.profileConfigService.overrideInputMapping[parsed.type] ?? defaultMapping[fieldType],
                    componentConfig: parsed.config ?? {}
                };
            }
        }

        const override = overrides?.[dottedKey];
        return {
            componentType: override?.type ?? defaultMapping[fieldType],
            componentConfig: override?.config ?? {}
        };
    }

    private parseControlOverride(controlOverride: string): any {
        try {
            return JSON.parse(controlOverride);
        } catch {
            return controlOverride;
        }
    }

    ngOnDestroy(): void {
        this.nestedIndex$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadChildComponent(
        componentType: Type<ShBaseGenericInputFieldComponent>,
        profileField: ExtendedBaseProfileField,
        formGroup: UntypedFormGroup,
        control: UntypedFormControl,
        records: PartialRecord[],
        config: Record<string, any>,
        isBatchEdit: boolean = false,
        nestedIndex: number,
        autocompleteFct: AutocompleteFunction | undefined
    ): void {
        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: 'config',
                    useValue: config
                }
            ]
        });

        const componentFactory: ComponentFactory<any> =
            this.componentFactoryResolver.resolveComponentFactory(componentType);

        const viewContainerRef: ViewContainerRef = this.adHost.viewContainerRef;
        viewContainerRef.clear();

        const contentProjections: any[][] = [];

        if (
            isBatchEdit &&
            !nestedIndex &&
            componentType !== ShUnsupportedInputFieldComponent &&
            !profileField.ReadOnly
        ) {
            const mergeStrategyControl: ComponentRef<ShMergeStrategyControlComponent> =
                this.createMergeStrategyControlComponent(viewContainerRef, profileField, formGroup);
            contentProjections.push([mergeStrategyControl.location.nativeElement]);
        }

        this.componentRef = viewContainerRef.createComponent(componentFactory, undefined, injector, contentProjections);

        this.componentRef.instance.profileField = profileField;
        this.componentRef.instance.records = records;
        this.componentRef.instance.control = control;
        this.componentRef.instance.errorMsg = control.getError('message');
        this.componentRef.instance.autocompleteFct = autocompleteFct;
        this.componentRef.instance.subFieldAutocompleteFcts = this.subFieldAutocompleteFcts;
        this.componentRef.instance.isBatchEdit = isBatchEdit;
        this.componentRef.instance.nestedIndex = nestedIndex;
        this.componentRef.instance.clearable = true; // MED-5187 clear radio buttons

        this.disabledMessageKey$
            .pipe(takeUntil(this.destroy$))
            .subscribe(disabledMessageKey => (this.componentRef.instance.disabledMessageKey = disabledMessageKey));

        control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.componentRef.instance.errorMsg = control.getError('message');
        });

        this.initialized$.next(true);
    }

    private createMergeStrategyControlComponent(
        viewContainerRef: ViewContainerRef,
        profileField: ExtendedBaseProfileField,
        formGroup: UntypedFormGroup
    ): ComponentRef<ShMergeStrategyControlComponent> {
        const mergeStrategyControlFactory: ComponentFactory<ShMergeStrategyControlComponent> =
            this.componentFactoryResolver.resolveComponentFactory(ShMergeStrategyControlComponent);
        const mergeStrategyControl: ComponentRef<ShMergeStrategyControlComponent> =
            viewContainerRef.createComponent(mergeStrategyControlFactory);

        mergeStrategyControl.instance.profileField = profileField;
        mergeStrategyControl.instance.formGroup = formGroup;

        return mergeStrategyControl;
    }

    private disableIfNeeded(control: FormControl, profileField: ExtendedBaseProfileField): void {
        if (profileField.ReadOnly || profileField.FieldDefinition.ReadOnly) {
            this.disabledMessageKey$.next('form.disable-reasons.readonly');
            control.disable();
        }

        if (profileField.Scope === ProfileFieldScope.Standard) {
            this.canEditStandard$.pipe(take(1), takeUntil(this.destroy$)).subscribe(canEdit => {
                if (!canEdit) {
                    this.disabledMessageKey$.next('form.disable-reasons.permissions');
                    control.disable();
                }
            });
        }

        if (profileField.Scope === ProfileFieldScope.Advanced) {
            this.canEditAdvanced$.pipe(take(1), takeUntil(this.destroy$)).subscribe(canEdit => {
                if (!canEdit) {
                    this.disabledMessageKey$.next('form.disable-reasons.permissions');
                    control.disable();
                }
            });
        }

        // No scope or scope: Public -> no action needed
    }

    // #region Inheritance and AI indicators
    private initIconConditions() {
        combineLatest([this.initialized$, this.profileField$, this.recordInheritance$, this.aiProfile$])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([initialized, profileField, recordInheritance, aiProfile]) => {
                const fieldInheritance = recordInheritance.getFieldInheritance(profileField.DottedKey);
                if (fieldInheritance) {
                    this.componentRef.instance.fieldInheritance = fieldInheritance;
                }

                if (
                    profileField.FieldDefinition.Family === Family.Ai ||
                    aiProfile?.Fields.find(aiField =>
                        this.matchingAIEnhancedField(profileField, aiField as AIProfileField)
                    )
                ) {
                    this.componentRef.instance.aiActive = true;
                }
            });
    }

    private matchingAIEnhancedField(profileField: ProfileField, aiProfileField: AIProfileField): boolean {
        const isAIEnhancedField: boolean = aiProfileField.GenerativeMetadata.Hint !== null;
        const isMatchingField: boolean =
            aiProfileField.DottedKey === profileField.DottedKey ||
            getParentDottedKey(aiProfileField.DottedKey) === profileField.DottedKey; // Needed for listfields

        return isAIEnhancedField && isMatchingField;
    }
    // #endregion
}
